import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { LoaderService } from './loading.service';
import { RequestCacheService } from './request-cache.service';
import { RollbarService } from './rollbar.service';
import { UserService } from './user.service';
import { AuthService } from './auth.service';

@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
  constructor(
    private loaderService: LoaderService,
    private cache: RequestCacheService,
    private injector: Injector,
    private userService: UserService,
    private authService: AuthService
  ) {}

  intercept<T>(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    this.loaderService.isLoading.next(true);

    // Skip auth headers for auth-related endpoints
    const isAuthEndpoint = req.url.includes('/entra/oauth-callback') ||
                           req.url.includes('/entra/refresh-token') ||
                           req.url.includes('/entra/login') ||
                           req.url.includes('/entra/logout');

    // Add auth headers if we have tokens and it's not an auth endpoint
    if (!isAuthEndpoint) {
      const accessToken = this.authService.getAccessToken();
      const idToken = this.authService.getIdToken();

      if (accessToken && idToken) {
        req = req.clone({
          setHeaders: {
            'x-access-token': accessToken,
            'x-id-token': idToken
          }
        });
      }
    }

    const cachedResponse = this.cache.get(req) as HttpResponse<T> | undefined;
    if (cachedResponse) {
      this.loaderService.isLoading.next(false);
      return of(cachedResponse);
    }

    return next.handle(req).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          this.cache.put(req, event);
          this.userService.resetAutoSignOut();
        }
        this.loaderService.isLoading.next(false);
      }),
      catchError((err: HttpErrorResponse) => {
        this.loaderService.isLoading.next(false);

        if (err.status === 401 && !isAuthEndpoint) {
          // Try to refresh token if we have a refresh token
          if (this.authService.getRefreshToken()) {
            return this.authService.refreshAccessToken().pipe(
              switchMap(() => {
                // Retry the original request with new token
                const accessToken = this.authService.getAccessToken();
                const idToken = this.authService.getIdToken();

                const retryReq = req.clone({
                  setHeaders: {
                    'x-access-token': accessToken || '',
                    'x-id-token': idToken || ''
                  }
                });

                return next.handle(retryReq);
              }),
              catchError(() => {
                // Refresh failed, redirect to login
                sessionStorage.setItem('auth_return_url', window.location.pathname);
                this.authService.redirectToLogin();
                return throwError(() => err);
              })
            );
          }

          // No refresh token, redirect to login
          sessionStorage.setItem('auth_return_url', window.location.pathname);
          this.authService.redirectToLogin();
          return throwError(() => err);
        }

        const rollbar = this.injector.get(RollbarService);
        let errorMessage: string;
        if (err.error instanceof ErrorEvent) {
          // Client-side error
          errorMessage = `Error: ${err.error.message}`;
        } else {
          // Server-side error
          errorMessage = `Error Code: ${err.status}\nMessage: ${err.message}`;
        }
        rollbar.error(errorMessage, err);
        throw err;
      })
    );
  }
}
